import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { createRoot } from "react-dom/client"
import {
  MUSICAL_CELEBRATION_MS,
  playMusicalCelebrationTone,
  runMusicalCelebration,
} from "../../helpers/musicalCelebration"
// Phoenix kept available: import { runPhoenixCelebration } from "../../helpers/phoenixCelebration"

export const TASK_COMPLETED_CELEBRATION_EVENT = "amok:task-completed"

const DURATION_MS = MUSICAL_CELEBRATION_MS + 200

let hostReady = false
let listenerReady = false
let pendingCelebration = false

const dispatchCelebration = () => {
  window.dispatchEvent(new CustomEvent(TASK_COMPLETED_CELEBRATION_EVENT))
}

/**
 * Fire Asana-style musical celebration (guitar + notes portal overlay).
 * Safe to call from status dropdown / checkboxes after marking Completed.
 * Phoenix animation remains in helpers/phoenixCelebration.js if we want it later.
 */
export const celebrateTaskCompleted = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return
  // Unlock + play soft tone on the same user gesture as the checkbox / dropdown
  playMusicalCelebrationTone()
  ensureHost()

  // First call mounts the host asynchronously — queue until the listener is attached
  // so the first completion still plays.
  if (!listenerReady) {
    pendingCelebration = true
    return
  }

  dispatchCelebration()
}

const ensureHost = () => {
  if (hostReady) return
  let node = document.getElementById("amok-task-completed-root")
  if (!node) {
    node = document.createElement("div")
    node.id = "amok-task-completed-root"
    document.body.appendChild(node)
  }
  createRoot(node).render(<TaskCompletedCelebrationHost />)
  hostReady = true
}

/**
 * Acoustic guitar + floating notes soar bottom-left → top-right, then glitter.
 */
const TaskCompletedCelebrationHost = () => {
  const [visible, setVisible] = useState(false)
  const [burstKey, setBurstKey] = useState(0)
  const canvasRef = useRef(null)
  const cancelRef = useRef(null)

  useEffect(() => {
    const onCelebrate = () => {
      setBurstKey(k => k + 1)
      setVisible(true)
    }

    window.addEventListener(TASK_COMPLETED_CELEBRATION_EVENT, onCelebrate)
    listenerReady = true

    if (pendingCelebration) {
      pendingCelebration = false
      onCelebrate()
    }

    return () => {
      window.removeEventListener(TASK_COMPLETED_CELEBRATION_EVENT, onCelebrate)
      listenerReady = false
    }
  }, [])

  useEffect(() => {
    if (!visible) return undefined

    let cancel = null
    let safety = 0
    let startRaf = 0

    const start = () => {
      const canvas = canvasRef.current
      if (!canvas) {
        startRaf = requestAnimationFrame(start)
        return
      }
      cancelRef.current?.()
      cancel = runMusicalCelebration(canvas, {
        onComplete: () => setVisible(false),
      })
      cancelRef.current = cancel
      safety = window.setTimeout(() => setVisible(false), DURATION_MS)
    }

    start()

    return () => {
      cancelAnimationFrame(startRaf)
      window.clearTimeout(safety)
      cancel?.()
      if (cancelRef.current === cancel) cancelRef.current = null
    }
  }, [visible, burstKey])

  if (!visible || typeof document === "undefined") return null

  return createPortal(
    <div
      key={burstKey}
      className="task-completed-celebration"
      role="status"
      aria-live="polite"
      aria-label="Task completed"
    >
      <canvas
        ref={canvasRef}
        className="task-completed-celebration__canvas"
        aria-hidden="true"
      />
    </div>,
    document.body,
  )
}

export default TaskCompletedCelebrationHost
